import React, { FC } from "react";

/**
 * Cloudinary delivery helper.
 *
 * The four source images have different native dimensions and orientations,
 * so we never ship the originals. Instead we insert a transformation segment
 * after /upload/ and let Cloudinary crop + resize + re-encode on the fly:
 *
 *   f_auto  → WebP/AVIF when the browser supports it
 *   q_auto  → automatic quality (usually 40-70% smaller than the original)
 *   c_fill  → crop to fill the requested box, no letterboxing
 *   g_auto  → content-aware crop, keeps faces/subject in frame
 *   ar_x:y  → forces a uniform aspect ratio across all four images
 *   w_N     → pixel width, driven by srcSet
 */
const CLOUD_BASE = "https://res.cloudinary.com/delmdmex1/image/upload";

const WIDTHS = [320, 480, 640, 800, 1000];

type Ratio = "1:1" | "4:5" | "16:9" | "3:2";

const cldUrl = (publicId: string, ratio: Ratio, width: number) =>
  `${CLOUD_BASE}/f_auto,q_auto,c_fill,g_auto,ar_${ratio},w_${width}/${publicId}`;

const cldSrcSet = (publicId: string, ratio: Ratio) =>
  WIDTHS.map((w) => `${cldUrl(publicId, ratio, w)} ${w}w`).join(", ");

/** Ratio → Tailwind aspect class, so the box is reserved before the image loads. */
const ASPECT_CLASS: Record<Ratio, string> = {
  "1:1": "aspect-square",
  "4:5": "aspect-[4/5]",
  "16:9": "aspect-video",
  "3:2": "aspect-[3/2]",
};

type GalleryImage = {
  publicId: string;
  alt: string;
  caption: string;
};

export const COMMUNITY_IMAGES: GalleryImage[] = [
  {
    publicId: "v1786736034/iquitos-trip-sunset-nomadroof_muxtd2.jpg",
    alt: "Exchange students watching the sunset over the river on the Iquitos trip",
    caption: "Iquitos",
  },
  {
    publicId: "v1786735985/old-money-nomadroof-events-party_xwhz3r.jpg",
    alt: "Guests dancing at a Nomadroof Old Money themed party in Lima",
    caption: "Party nights",
  },
  {
    publicId: "v1786735995/iquitos-trip-nomadroof-boat_yki3xe.jpg",
    alt: "Group of students on a boat during the Amazon trip near Iquitos",
    caption: "Amazon trips",
  },
  {
    publicId: "v1786735977/old-money-nomadroof-blackjack-event_mt9auc.jpg",
    alt: "Students playing blackjack at a Nomadroof casino night event",
    caption: "Game nights",
  },
];

/** The first tile is the likely LCP element — preload it at a sensible width. */
export const heroPreloadHref = cldUrl(COMMUNITY_IMAGES[0].publicId, "1:1", 480);

const CommunityGallery: FC = () => (
  <div className="mt-8">
    <div className="grid grid-cols-2 gap-2.5">
      {COMMUNITY_IMAGES.map((img, index) => (
        <figure
          key={img.publicId}
          className={`relative overflow-hidden rounded-2xl bg-neutral-100 dark:bg-neutral-800 ${ASPECT_CLASS["1:1"]}`}
        >
          <img
            src={cldUrl(img.publicId, "1:1", 480)}
            srcSet={cldSrcSet(img.publicId, "1:1")}
            sizes="(min-width: 448px) 224px, 50vw"
            alt={img.alt}
            width={480}
            height={480}
            loading={index < 2 ? "eager" : "lazy"}
            decoding="async"
            // @ts-ignore — fetchPriority landed in React 18.3 types
            fetchpriority={index === 0 ? "high" : undefined}
            className="h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-3 pb-2.5 pt-8">
            <figcaption className="text-xs font-semibold uppercase tracking-wide text-white">
              {img.caption}
            </figcaption>
          </div>
        </figure>
      ))}
    </div>
    <p className="mt-3 text-center text-xs text-neutral-500">
      Real trips and events from past semesters.
    </p>
  </div>
);

export default CommunityGallery;